import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  IconButton,
  Grid,
  Card,
  CardContent,
  Box,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import {
  getCompanyEnvironments,
  assignEnvironmentToCompany,
  removeEnvironmentFromCompany,
  getItems,
  getCompanies,
  getUnlinkedEnvironments,
} from "../services/api";
import { SubTitle, Title } from "./common/Title";

const CompanyEnvironmentManagement = ({ token }) => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [environments, setEnvironments] = useState([]);
  const [allEnvironments, setAllEnvironments] = useState([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    fetchCompanies();
    fetchAllEnvironments();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchCompanyEnvironments(selectedCompany);
    }
  }, [selectedCompany]);

  const fetchCompanies = async () => {
    try {
      const data = await getCompanies(token);
      setCompanies(data);
    } catch {
      setError("Erro ao buscar empresas.");
    }
  };

  const fetchCompanyEnvironments = async (companyId) => {
    try {
      const data = await getCompanyEnvironments(token, companyId);
      setEnvironments(data);
    } catch {
      setError("Erro ao buscar ambientes da empresa.");
    }
  };

  const fetchAllEnvironments = async () => {
    try {
      const data = await getUnlinkedEnvironments(token);
      setAllEnvironments(data);
    } catch {
      setError("Erro ao buscar todos os ambientes.");
    }
  };

  const handleAssignEnvironment = async () => {
    if (!selectedCompany || !selectedEnvironment) {
      setError("Selecione uma empresa e um ambiente.");
      return;
    }
    try {
      await assignEnvironmentToCompany(
        token,
        selectedCompany,
        selectedEnvironment
      );
      fetchCompanyEnvironments(selectedCompany);
      setSelectedEnvironment("");
      fetchAllEnvironments();
    } catch {
      setError("Erro ao vincular ambiente à empresa.");
    }
  };

  const handleRemoveEnvironment = async (environmentId) => {
    try {
      await removeEnvironmentFromCompany(token, selectedCompany, environmentId);
      fetchCompanyEnvironments(selectedCompany);
      fetchAllEnvironments();
    } catch {
      setError("Erro ao remover ambiente da empresa.");
    }
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Title>🌎 Conexão Empresa x Ambiente</Title>

      {error && <Alert severity="error">{error}</Alert>}

      {/* Seção de Seleção de Empresa */}
      <Card elevation={3} sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <SubTitle>🏢 Selecione a empresa</SubTitle>
          <FormControl fullWidth>
            <InputLabel>Empresa</InputLabel>
            <Select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
            >
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Mostrar apenas se houver empresa selecionada */}
      {selectedCompany && (
        <>
          <Grid container spacing={3}>
            {/* Ambientes Vinculados */}
            <Grid item xs={12} md={6}>
              <Card elevation={3} sx={{ borderRadius: 3 }}>
                <CardContent>
                  <SubTitle>✅ Ambientes vinculados</SubTitle>
                  <TableContainer>
                    <Table>
                      <TableHead sx={{ backgroundColor: "#1976d2" }}>
                        <TableRow>
                          <TableCell
                            sx={{ color: "white", fontWeight: "bold" }}
                          >
                            Alias
                          </TableCell>
                          <TableCell
                            sx={{ color: "white", fontWeight: "bold" }}
                          >
                            Nome do ambiente
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "white",
                              fontWeight: "bold",
                              textAlign: "center",
                            }}
                          >
                            Ações
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {environments.length > 0 ? (
                          environments.map((env) => (
                            <TableRow key={env.AliasAccess}>
                              <TableCell>{env.AliasAccess}</TableCell>
                              <TableCell>{env.NomeCli}</TableCell>
                              <TableCell align="center">
                                <IconButton
                                  color="error"
                                  onClick={() =>
                                    handleRemoveEnvironment(env.AliasAccess)
                                  }
                                >
                                  <Delete sx={{ fontSize: 20 }} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              <Typography variant="body1" color="textSecondary">
                                Nenhum ambiente vinculado.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Vincular Novo Ambiente */}
            <Grid item xs={12} md={6}>
              <Card elevation={3} sx={{ borderRadius: 3 }}>
                <CardContent>
                  <SubTitle>➕ Vincular ambiente</SubTitle>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Ambiente</InputLabel>
                    <Select
                      value={selectedEnvironment}
                      onChange={(e) => setSelectedEnvironment(e.target.value)}
                    >
                      {allEnvironments.map((env) => (
                        <MenuItem key={env.AliasAccess} value={env.AliasAccess}>
                          {env.AliasAccess}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleAssignEnvironment}
                  >
                    Vincular ambiente
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};

export default CompanyEnvironmentManagement;
